import type { Decade, Genre } from "../../shared/domain.ts";

/**
 * How genres and eras are named and coloured on screen.
 *
 * A plain module rather than part of a component file: exporting these alongside
 * a component broke Fast Refresh ("decadeLabel export is incompatible") and made
 * every edit to the picker a full reload.
 */

export function genreLabel(genre: Genre): string {
  return genre === "hip-hop"
    ? "Hip Hop"
    : genre === "rnb"
      ? "R&B"
      : genre[0]!.toUpperCase() + genre.slice(1);
}

export function decadeLabel(decade: Decade): string {
  return decade === "pre-2000s" ? "90s & older" : decade;
}

/**
 * A glyph and a hue per genre.
 *
 * The genre picker was nine identical grey pills of text, which is the least
 * scannable way to present a choice between nine things. Colour does most of the
 * work here — the glyph is a bonus, and deliberately not load-bearing, because
 * emoji render differently on every platform and some render flat.
 */
export interface GenreVisual {
  glyph: string;
  /** An HSL hue, used as `hsl(<hue> 70% 58%)`. */
  hue: number;
}

export const GENRE_VISUALS: Record<Genre, GenreVisual> = {
  pop: { glyph: "✨", hue: 322 },
  rock: { glyph: "🎸", hue: 6 },
  "hip-hop": { glyph: "🎤", hue: 42 },
  electronic: { glyph: "🎛️", hue: 189 },
  rnb: { glyph: "🌙", hue: 268 },
  alternative: { glyph: "🌀", hue: 158 },
  metal: { glyph: "🤘", hue: 220 },
  latin: { glyph: "💃", hue: 24 },
  country: { glyph: "🤠", hue: 92 },
};

/** Eras get a hue too, so the two rows of the picker read as one system. */
export const DECADE_HUES: Record<Decade, number> = {
  "2020s": 172,
  "2010s": 205,
  "2000s": 250,
  "pre-2000s": 292,
};
